import { createContext, useContext, type ReactNode, type CSSProperties } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import type { TableDensity } from "../DataTable/DataTable.types";

const TableDensityContext = createContext<TableDensity>("default");

/* Exact compiled output of the pre-migration Tailwind utilities. The
 * named-group row hover (`group-hover/row:bg-surface-app` on cells) becomes
 * `&:hover > td` on the row — the identical element set with winning
 * specificity. */

const densityStyles: Record<TableDensity, { header: CSSObject; cell: CSSObject; text: CSSObject }> = {
  compact: {
    header: { paddingLeft: ".75rem", paddingRight: ".75rem", paddingTop: ".5rem", paddingBottom: ".5rem" },
    cell: { paddingLeft: ".75rem", paddingRight: ".75rem", paddingTop: ".5rem", paddingBottom: ".5rem" },
    text: { fontSize: ".75rem", lineHeight: "1rem" }
  },
  default: {
    header: { paddingLeft: "1rem", paddingRight: "1rem", paddingTop: ".625rem", paddingBottom: ".625rem" },
    cell: { paddingLeft: "1rem", paddingRight: "1rem", paddingTop: ".75rem", paddingBottom: ".75rem" },
    text: { fontSize: ".875rem", lineHeight: "1.25rem" }
  },
  comfortable: {
    header: { paddingLeft: "1.25rem", paddingRight: "1.25rem", paddingTop: ".75rem", paddingBottom: ".75rem" },
    cell: { paddingLeft: "1.25rem", paddingRight: "1.25rem", paddingTop: "1rem", paddingBottom: "1rem" },
    text: { fontSize: ".875rem", lineHeight: "1.25rem" }
  }
};

export interface TableProps {
  children?: ReactNode;
  density?: TableDensity;
  minWidth?: number | string;
  maxHeight?: number | string;
  className?: string;
  tableClassName?: string;
  "aria-label"?: string;
}

const TableRoot = styled("div", {
  label: "AgenticTable-root"
})({
  overflow: "hidden",
  borderRadius: "var(--bfrs-radius-md)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
});

const TableScroller = styled("div", {
  label: "AgenticTable-scroller"
})({
  overflow: "auto"
});

const TableElement = styled("table", {
  label: "AgenticTable-table"
})({
  width: "100%",
  borderCollapse: "separate",
  "--tw-border-spacing-x": "0px",
  "--tw-border-spacing-y": "0px",
  borderSpacing: "var(--tw-border-spacing-x) var(--tw-border-spacing-y)",
  textAlign: "left"
});

export function Table({
  children,
  density = "default",
  minWidth,
  maxHeight,
  className,
  tableClassName,
  "aria-label": ariaLabel = "Table"
}: TableProps) {
  const scrollStyle: CSSProperties = maxHeight !== undefined ? { maxHeight } : {};
  const tableStyle: CSSProperties = minWidth !== undefined ? { minWidth } : {};

  return (
    <TableDensityContext.Provider value={density}>
      <TableRoot className={className}>
        <TableScroller style={scrollStyle}>
          <TableElement aria-label={ariaLabel} className={tableClassName} style={tableStyle}>
            {children}
          </TableElement>
        </TableScroller>
      </TableRoot>
    </TableDensityContext.Provider>
  );
}

export interface TheadProps {
  children?: ReactNode;
  sticky?: boolean;
  className?: string;
}

const TheadRoot = styled("thead", {
  label: "AgenticTable-thead",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { sticky: boolean } }>(({ ownerState }) => [
  ownerState.sticky && {
    position: "sticky",
    top: 0,
    zIndex: 30
  }
]);

export function Thead({ children, sticky = false, className }: TheadProps) {
  return (
    <TheadRoot ownerState={{ sticky }} className={className}>
      {children}
    </TheadRoot>
  );
}

export interface TbodyProps {
  children?: ReactNode;
  className?: string;
}

export function Tbody({ children, className }: TbodyProps) {
  return <tbody className={className}>{children}</tbody>;
}

export interface TfootProps {
  children?: ReactNode;
  className?: string;
}

export function Tfoot({ children, className }: TfootProps) {
  return <tfoot className={className}>{children}</tfoot>;
}

export interface TrProps {
  children?: ReactNode;
  onClick?: () => void;
  className?: string;
}

const TrRoot = styled("tr", {
  label: "AgenticTable-tr",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { clickable: boolean } }>(({ ownerState }) => [
  {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-surface-app) / var(--tw-bg-opacity, 1))"
    },
    "&:hover > td": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-surface-app) / var(--tw-bg-opacity, 1))"
    }
  },
  ownerState.clickable && { cursor: "pointer" }
]);

export function Tr({ children, onClick, className }: TrProps) {
  return (
    <TrRoot ownerState={{ clickable: Boolean(onClick) }} className={className} onClick={onClick}>
      {children}
    </TrRoot>
  );
}

export interface ThProps {
  children?: ReactNode;
  align?: "left" | "center" | "right";
  colSpan?: number;
  className?: string;
}

type CellAlign = "left" | "center" | "right";

const ThRoot = styled("th", {
  label: "AgenticTable-th",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { density: TableDensity; align: CellAlign } }>(({ ownerState }) => [
  {
    height: "38px",
    borderBottomWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-app) / var(--tw-bg-opacity, 1))",
    verticalAlign: "middle",
    fontSize: "10.5px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".07em",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
  },
  densityStyles[ownerState.density].header,
  ownerState.align === "center" && { textAlign: "center" },
  ownerState.align === "right" && { textAlign: "right" }
]);

export function Th({ children, align = "left", colSpan, className }: ThProps) {
  const density = useContext(TableDensityContext);
  return (
    <ThRoot scope="col" colSpan={colSpan} ownerState={{ density, align }} className={className}>
      {children}
    </ThRoot>
  );
}

export interface TdProps {
  children?: ReactNode;
  align?: "left" | "center" | "right";
  colSpan?: number;
  className?: string;
}

const TdRoot = styled("td", {
  label: "AgenticTable-td",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { density: TableDensity; align: CellAlign } }>(({ ownerState }) => [
  {
    borderBottomWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    verticalAlign: "middle",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
  },
  densityStyles[ownerState.density].cell,
  densityStyles[ownerState.density].text,
  ownerState.align === "center" && { textAlign: "center" },
  ownerState.align === "right" && { textAlign: "right" }
]);

export function Td({ children, align = "left", colSpan, className }: TdProps) {
  const density = useContext(TableDensityContext);
  return (
    <TdRoot colSpan={colSpan} ownerState={{ density, align }} className={className}>
      {children}
    </TdRoot>
  );
}
